<template>
  <div
    ref="container"
    class="menu"
    :class="containerModifierClass"
    @keydown.esc.capture="rememberOpenOnEscape"
    @keydown.esc="onKeyDownEscape"
  >
    <div v-if="!isPermanent" ref="activator" @click.prevent.stop="onActivatorClick">
      <slot name="activator" class="menu-activator" />
    </div>

    <sp-bottom-sheet
      v-if="showBottomSheet"
      :label="label"
      :open="isActive"
      :no-restore-focus="noRestoreFocus"
      :permanent="isPermanent"
      :expand-height-max="mobileExpandHeightMax"
      @close="onSurfaceClosed"
      @click.prevent.stop="onContentClick"
    >
      <slot />
      <slot slot="action" name="sheet-action">
        <sp-button class="bottom-sheet-close-action" color="primary">{{ t("menu_close") }}</sp-button>
      </slot>
    </sp-bottom-sheet>

    <sp-popup
      v-else
      ref="popup"
      :active="isActive"
      :no-restore-focus="noRestoreFocus"
      :location="location"
      :pointer="pointer"
      :permanent="isPermanent"
      :disabled="disabled"
      :anchor.prop="activator"
      @click.prevent.stop="onContentClick"
      @open="onPopupShown"
      @close="onSurfaceClosed"
    >
      <slot />
    </sp-popup>
  </div>
</template>

<script setup>
/**
 * The SpMenu component shows a menu at the position of the element used to activate it.
 *
 * @displayName Menu
 * @group Custom Elements
 * @component sp-menu
 *
 * @slot default - The menu's content.
 * @slot activator - The element that opens the menu when clicked.
 * @slot sheet-action - The bottom sheet's action, below the sm breakpoint. Defaults to a close
 *   button.
 */
import { onClickOutside, useEventListener, watchImmediate } from "@vueuse/core";
import { computed, nextTick, ref, toRef, useTemplateRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useDisabled } from "#ce/composables/useDisabled";
import { resolveActivatorControl } from "#ce/utils/activator-control";
import { t } from "#ce/shared/components-i18n";
import { deepActiveElement, focusWithin } from "#ce/utils/focus";
import { toBoolean } from "#ce/utils/props";
import { props as menuProps } from "./props.js";

const emit = defineEmits(["input", "open", "close", "update-open"]);

const props = defineProps(menuProps);

const isActive = ref(toBoolean(props.open));
const isPermanent = ref(toBoolean(props.permanent));

watch(isActive, (value) => {
  emit("update-open", value);
});

watchImmediate(
  () => [props.open, props.value],
  () => (isActive.value = toBoolean(props.open)),
);

const closeOnContentClick = computed(() => toBoolean(props.closeOnContentClick));
const closeOnOutsideClick = computed(() => toBoolean(props.closeOnOutsideClick));
const { disabled } = useDisabled(toRef(props, "disabled"));

const container = useTemplateRef("container");

const { isGtSmScreen, ltModifiers } = useBreakpoints();

const containerModifierClass = computed(() => [...ltModifiers.value]);

// `open` says the popup is visible, which is when its content can take focus. `update-open` fires
// earlier, when the open state changes. The bottom sheet moves focus itself and emits no `open`.
function onPopupShown() {
  emit("open");
}

async function onSurfaceClosed() {
  isActive.value = false;
  // The native surface is closed; let its closed-state classes render before handoff.
  await nextTick();
  if (!isActive.value) {
    returnFocus();
    emit("close");
  }
}

function onActivatorClick() {
  // sp-menu is not form-associated, so it must enforce its own disabled state —
  // there is no native :disabled suppression to fall back on.
  if (disabled.value) {
    return;
  }

  isActive.value = !isActive.value;

  // Safari leaves a clicked button unfocused, and Escape only reaches a menu that holds focus.
  if (isActive.value && !showBottomSheet.value && !focusWithin(activator.value)) {
    activatorControl()?.focus({ preventScroll: true });
  }
}

function onContentClick() {
  if (closeOnContentClick.value && isActive) {
    isActive.value = false;
  }
}

const activator = useTemplateRef("activator");
const popup = useTemplateRef("popup");

let clickOutsideStop;
const showBottomSheet = computed(() => !isPermanent.value && !isGtSmScreen.value);

let closedByOutsideClick = false;

function closeFromOutsideClick() {
  closedByOutsideClick = true;
  isActive.value = false;
}

watch(isActive, (value) => {
  if (!closeOnOutsideClick.value || showBottomSheet.value) {
    return;
  }
  if (value) {
    clickOutsideStop = onClickOutside(container, closeFromOutsideClick);
  } else if (clickOutsideStop) {
    clickOutsideStop();
  }
});

let opener = null;

function rememberOpener(open) {
  if (open) {
    opener = deepActiveElement();
    closedByOutsideClick = false;
  }
}

watchImmediate(isActive, rememberOpener);

function activatorControl() {
  const [slotted] = activator.value?.querySelector("slot")?.assignedElements({ flatten: true }) ?? [];

  return resolveActivatorControl(slotted);
}

// Returns focus to the element that had it when the menu opened, or to the activator's control when
// that element is `body` or cannot take focus. Does nothing unless focus sits inside the popup or on
// `body`. Also does nothing after an outside click, under `no-restore-focus`, and for the bottom
// sheet, because `sp-bottom-sheet` returns focus itself. After an outside click, focus stays where
// the click put it.
function returnFocus() {
  const remembered = opener;
  opener = null;

  if (showBottomSheet.value || toBoolean(props.noRestoreFocus) || closedByOutsideClick) {
    return;
  }

  const active = deepActiveElement();
  if (active && active !== document.body && !focusWithin(popup.value)) {
    return;
  }

  for (const candidate of [remembered, activatorControl()]) {
    if (candidate && candidate !== document.body) {
      candidate.focus?.({ preventScroll: true });
      if (focusWithin(candidate)) {
        return;
      }
    }
  }
}

let openOnEscape = false;

// Read during capture, because a consumer such as `sp-select` closes the menu from its own Escape listener
// before the press bubbles to `onKeyDownEscape`.
function rememberOpenOnEscape() {
  openOnEscape = isActive.value;
}

let escapeReleasePending = false;

// The legacy `window.Modal` closes on the `keyup` of an Escape press.
function stopEscapeKeyup(event) {
  if (escapeReleasePending && event.key === "Escape") {
    event.stopPropagation();
    escapeReleasePending = false;
  }
}

// A key released while the window is out of focus sends the page no `keyup`.
function forgetEscapePress() {
  escapeReleasePending = false;
}

useEventListener(window, "keyup", stopEscapeKeyup, true);
useEventListener(window, "blur", forgetEscapePress);

// Escape closes this menu and nothing around it.
function onKeyDownEscape(event) {
  if (isPermanent.value || !openOnEscape) {
    return;
  }

  event.stopPropagation();
  escapeReleasePending = true;

  // The bottom sheet's native dialog closes itself. A popup cancels the press, because a surrounding
  // `<dialog>` closes on any Escape that is not canceled.
  if (!showBottomSheet.value) {
    isActive.value = false;
    event.preventDefault();
  }
}
</script>

<style>
:host {
  display: block;
  width: 100%;
}

:host([permanent]) {
  height: 100%;
}
</style>

<style scoped lang="scss">
.menu {
  position: relative;

  :host([permanent]) & {
    height: 100%;
  }
}
.menu-activator {
  cursor: pointer;
  display: block;
  position: relative;
}
sp-bottom-sheet {
  --bottom-sheet-border-radius: 8px 8px 0 0;
  --sp-comp-bottom-sheet-content-padding-inline: 0;
  --sp-comp-bottom-sheet-content-padding-block: 0.5rem;
  --sp-comp-bottom-sheet-action-container-padding-inline: 0.5rem;
}
.bottom-sheet-close-action {
  --button-width: 100%;
}
</style>
